import React from 'react';

import classNames from 'classnames';

import { BlurrableImage } from '../BlurrableImage/BlurrableImage';
import { VectorImage } from '../VectorImage/VectorImage';
import { AssetStoryblok } from '../../../../types/storyblok';

export type StoryblokSrcSetImageProps = {
  src: AssetStoryblok | undefined;
  maxWidth: number;
  alt: string;
  aspectRatio: number | undefined;
  className: string;
};

// MARKUP
export const StoryblokSrcSetImage = ({
  src,
  alt,
  maxWidth,
  aspectRatio, // Default to 'natural/original' image aspect ratio
  className,
}: StoryblokSrcSetImageProps) => {
  // Abort if no asset has been passed in
  if (!src) {
    return null;
  }

  // VECTORS
  // Render a basic img element if the asset is an svg
  const filename = src?.filename;

  if (filename?.slice(-4) === '.svg') {
    return (
      <VectorImage src={src || ''} alt={alt || ''} className={className} />
    );
  }

  // BITMAPS
  // Round max width to nearest hundred to match Sanity's pre-generated assets. Rendered image width is handled seperately
  const roundedUpMaxWidth = Math.ceil(maxWidth / 100) * 100;

  // Set minimum asset width
  const minAssetWidth = 100;
  const BLURRING = 100;

  const blurredImageAsset = (): string => {
    if (!aspectRatio) {
      // Generate url and push to array
      return `${src?.filename}/m/100x0/filters:blur(10)`;
    }
    return `${src?.filename}/m/100x${Math.floor(
      100 / aspectRatio
    )}/filters:blur(10)
`;
  };

  const srcSetSizes = () => {
    let size = minAssetWidth;
    const widths = [];
    while (size <= roundedUpMaxWidth) {
      // Generate size option and push to array
      widths.push(`${size}w`);

      // Bump to next asset size
      size += 100;
    }

    return `(max-width: ${maxWidth}px) ${String(
      widths?.map((width) => width)
    )}`;
  };

  const srcSetAssets = () => {
    let size = minAssetWidth;
    const assetUrls = [];

    while (size <= roundedUpMaxWidth) {
      if (!aspectRatio) {
        // Generate url
        const url = `${src?.filename}/m/${size}x0/`;

        // Push to array
        assetUrls.push(`${url && url} ${size && size}w`);
      } else {
        const url = `${src?.filename}/m/${size}x${Math.floor(
          size / aspectRatio
        )}/`;

        // Push to array
        assetUrls.push(`${url && url} ${size && size}w`);
      }

      // Bump to next asset size
      size += 100;
    }

    return `${String(assetUrls.map((assetUrl) => assetUrl))}`;
  };

  return (
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    <div className={classNames('storyblok-image-wrapper', className)}>
      <BlurrableImage
        img={
          <img
            sizes={srcSetSizes()}
            srcSet={srcSetAssets()}
            className={className}
            loading="lazy"
            alt={alt || ''}
          />
        }
        blurredAssetUrl={blurredImageAsset()}
        alt={alt}
      />
    </div>
  );
};
